import axios from 'axios'
import { Link } from 'react-router-dom'
import { render } from '@react-email/components'
import { toast } from 'react-toastify'

import React, { Fragment, useState } from 'react'
import { Envelope, InstagramLogo, LinkedinLogo, WhatsappLogo } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import Button from 'src/component/core/button'
import InputText from 'src/component/core/input/input-text'
import InputTextArea from 'src/component/core/input/input-textarea'
import Select from 'src/component/core/select'

import Banner from './Banner'
import ContactUsEmail from './ContactUsEmail'

export default function ContactUsPage() {
    const [getLoading, setLoading] = useState(false)

    const [getCompanyName, setCompanyName] = useState('')
    const [getCountry, setCountry] = useState('')
    const [getEmail, setEmail] = useState('')
    const [getFirstName, setFirstName] = useState('')
    const [getMessage, setMessage] = useState('')
    const [getLastName, setLastName] = useState('')
    const [getPhone, setPhone] = useState('')
    const [getService, setService] = useState(null)
    const [getSubject, setSubject] = useState('')

    const { t } = useTranslation()

    const SubmitForm = async (e) => {
        e.preventDefault()
        setLoading(true)

        await axios
            .post(process.env.REACT_APP_WEB_URL + '/submit-form-complaint', {
                from: getEmail,
                to: process.env.REACT_APP_EMAIL_DESTINATION,
                subject: getSubject,
                message: render(
                    <ContactUsEmail
                        companyName={getCompanyName}
                        country={getCountry}
                        email={getEmail}
                        firstName={getFirstName}
                        message={getMessage}
                        lastName={getLastName}
                        phone={getPhone}
                        service={getService?.value}
                    />
                ),
            })
            .catch((err) => {
                toast.error('Failed to submit form!')
                console.log(err)
            })
            .finally(() => {
                setCompanyName('')
                setCountry('')
                setEmail('')
                setFirstName('')
                setMessage('')
                setLastName('')
                setPhone('')
                setSubject('')
                setService(null)

                toast.success('Your form has been submitted')
                setLoading(false)
            })
    }

    const iconSize = 32

    return (
        <Fragment>
            <Banner />

            <div className="max-w-[1280px] mx-auto w-full relative px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-24">
                <div className="grid gap-8">
                    <div>
                        <h2 className="font-black text-secondary text-2xl sm:text-3xl lg:text-4xl">
                            {t('CONNECT WITH US!')}
                        </h2>
                        <p className="text-secondary mt-2 text-sm sm:text-base leading-relaxed max-w-[75ch]">
                            {t('Your trusted partner for certification, validation and verification of environmental projects.')}
                        </p>
                    </div>

                    {/* Main layout: form + info */}
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10 items-start w-full min-w-0">
                        {/* Form */}
                        <form className="grid gap-5 sm:gap-6 w-full min-w-0" onSubmit={SubmitForm}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 w-full min-w-0">
                                <InputText
                                    label={t('Company Name')}
                                    onChange={(e) => setCompanyName(e?.target?.value)}
                                    required={true}
                                    value={getCompanyName}
                                />
                                <InputText
                                    label={t('Work Email Address')}
                                    onChange={(e) => setEmail(e?.target?.value)}
                                    required={true}
                                    type="email"
                                    value={getEmail}
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 w-full min-w-0">
                                <InputText
                                    label={t('First Name')}
                                    onChange={(e) => setFirstName(e?.target?.value)}
                                    required={true}
                                    value={getFirstName}
                                />
                                <InputText
                                    label={t('Last Name')}
                                    onChange={(e) => setLastName(e?.target?.value)}
                                    required={true}
                                    value={getLastName}
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 w-full min-w-0">
                                <InputText
                                    label={t('Country')}
                                    onChange={(e) => setCountry(e?.target?.value)}
                                    required={true}
                                    value={getCountry}
                                />
                                <InputText
                                    label={t('Phone Number')}
                                    onChange={(e) => setPhone(e?.target?.value)}
                                    required={true}
                                    type="number"
                                    value={getPhone}
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 w-full min-w-0">
                                <InputText
                                    label={t('Subject')}
                                    onChange={(e) => setSubject(e?.target?.value)}
                                    required={true}
                                    value={getSubject}
                                />
                                <Select
                                    label={t('Services')}
                                    value={getService}
                                    onChange={(val) => setService(val)}
                                    options={[
                                        { value: 'verifikasi_organisasi', label: t('GHG Organization Level Verification') },
                                        { value: 'verifikasi_projek_grk', label: t('GHG Project Level Verification') },
                                        { value: 'validasi_projek_grk', label: t('GHG Project Level Validation') },
                                    ]}
                                />
                            </div>

                            <InputTextArea
                                label={t('Message')}
                                onChange={(e) => setMessage(e?.target?.value)}
                                required={true}
                                value={getMessage}
                            />

                            <Button
                                type="submit"
                                classNameAdditional="bg-secondary font-semibold py-3 px-10 sm:px-24 text-white w-full sm:w-fit"
                                disabled={[getCompanyName, getCountry, getFirstName, getLastName, getEmail, getPhone, getSubject, getMessage, getService].some(
                                    (item) => !item
                                )}
                                isLoading={getLoading}
                            >
                                {t('Send')}
                            </Button>
                        </form>

                        {/* Info column (responsive) */}
                        <div className="w-full min-w-0">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-8">
                                <div>
                                    <h5 className="font-bold text-secondary">{t('Address')}:</h5>
                                    <Link
                                        to={
                                            'https://www.google.com/maps/place/Citylofts+Sudirman/@-6.209024,106.8158243,17z/data=!3m1!4b1!4m6!3m5!1s0x2e69f40284aeba2d:0x8523f9ff207a98e7!8m2!3d-6.209024!4d106.8183992!16s%2Fg%2F1td1y1pq?entry=ttu'
                                        }
                                        target="_blank"
                                    >
                                        <p className="leading-6 text-sm sm:text-base break-words">
                                            Cityloft Sudirman Floor 27 Unit 2717
                                            <br />
                                            Jl. K.H. Mas Mansyur No.121, RT.10/RW.11
                                            <br />
                                            Karet Tengsin, Kecamatan Tanah Abang, Kota Jakarta Pusat
                                            <br />
                                            Daerah Khusus Ibukota Jakarta 10250
                                        </p>
                                    </Link>
                                </div>
                                <div className="flex flex-col gap-5">
                                    <div>
                                        <h5 className="font-bold text-secondary">{t('Phone')}:</h5>
                                        <Link to={'https://wa.me/+6285935000970'} className="text-sm sm:text-base break-words">
                                            (+62) 859-3500-0970
                                        </Link>
                                    </div>

                                    <div>
                                        <h5 className="font-bold text-secondary">Email:</h5>
                                        <Link to={'mailto:info@naturetrust.id'} className="text-sm sm:text-base break-all">
                                            info@naturetrust.id
                                        </Link>
                                    </div>

                                    <div className="flex flex-wrap gap-4 items-center pt-2">
                                        <Link to={'https://www.linkedin.com/company/100936494/admin/inbox/'}>
                                            <LinkedinLogo color="#205872" size={iconSize} weight="fill" />
                                        </Link>
                                        <Link to={'mailto:info@naturetrust.id'}>
                                            <Envelope color="#205872" size={iconSize} weight="fill" />
                                        </Link>
                                        <Link to={'https://wa.me/+6285935000970'}>
                                            <WhatsappLogo color="#205872" size={iconSize} weight="fill" />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* end info */}
                    </div>
                </div>
            </div>
        </Fragment>
    )
}
